import JSBridge from '@easyvaas/js-bridge';
import { Toast } from '@easyvaas/js-components';
import { useSearchParams } from '@easyvaas/react-hooks';
import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SmallText from 'src/components/SmallText';
import ImageStars from '../../components/ImageStars';
import {
  fetchFightInfo,
  inspireImage,
  setDefaultImage,
  startFight
} from '../../services';
import styles from './index.module.scss';

interface ItemProps {
  item: ImageV3API.Image;
  anchor?: string | null;
  inspire?: number | null;
}
const ImageItem: React.FC<ItemProps> = ({ item, anchor, inspire }) => {
  const type = useSearchParams('type') || '1';
  return (
    <div className={styles.item}>
      <div
        className={styles.logo}
        style={{ backgroundImage: `url(${item.url})` }}
      >
        {type === '2' && anchor && (
          <SmallText fontSize={29} className={styles.name} inline>
            {anchor}
          </SmallText>
        )}
      </div>
      <div>
        <div className={styles.title}>{item.title}</div>
        <ImageStars count={item.upStar?.level || 0} />
        <SmallText fontSize={26} className={styles.prop} inline>
          攻击力：{item.attackValue}
        </SmallText>
        <SmallText fontSize={26} className={styles.prop} inline>
          生命值：{item.healthPoint}
        </SmallText>
        <SmallText fontSize={29} className={styles.fighting} inline>
          战力 {item.fightingValue}
        </SmallText>
        {inspire && (
          <SmallText fontSize={29} className={styles.morale} inline>
            士气 {inspire}
          </SmallText>
        )}
      </div>
    </div>
  );
};

const instanceOfBuff = (obj: any): obj is ImageV3API.Buff => {
  return obj.level !== undefined;
};

interface StatusItemProps {
  item?: ImageV3API.Buff | ImageV3API.Ability;
}
const StatusItem: React.FC<StatusItemProps> = ({ item }) => {
  const type = useSearchParams('type') || '1';
  if (!item) return null;
  if (instanceOfBuff(item) && item.level === 0) return null;
  const getLabel = () => {
    if (instanceOfBuff(item)) {
      return '状态';
    } else if (item.isHigh) {
      return '高级天赋';
    } else {
      return '普通天赋';
    }
  };
  return (
    <div className={styles.status}>
      {type === '1' && (
        <SmallText fontSize={26} className={styles.label}>
          {getLabel()}
        </SmallText>
      )}
      <div
        className={styles.logo}
        style={{
          backgroundImage: `url(${item.logo})`
        }}
      />
      {type === '1' ? (
        <SmallText fontSize={26} className={styles.text}>
          {item.title}
          {instanceOfBuff(item) && `(${item.level})层`}：{item.desc}
        </SmallText>
      ) : (
        <>
          <SmallText fontSize={26} className={styles.text}>
            {item.title}
            {instanceOfBuff(item) && `(${item.level})层`}
          </SmallText>
          <SmallText fontSize={26} className={styles.text}>
            {item.desc}
          </SmallText>
        </>
      )}
    </div>
  );
};
const Popup: React.FC = () => {
  const [state, setState] = useState<ImageV3API.FightInfo>();
  const [current, setCurrent] = useState<ImageV3API.Image>();
  const anchorName = useSearchParams('anchorName');
  const vid = useSearchParams('vid');
  const type = useSearchParams('type') || '1';
  const location = useLocation();
  const history = useHistory();
  const fetch = useCallback(async () => {
    try {
      const res = await fetchFightInfo({ anchorName });
      if (!res.toUserImage) {
        JSBridge.call('closePopup', '');
      } else {
        setState(res);
        if (res.fromUserImage) {
          setCurrent(res.fromUserImage);
        }
      }
    } catch {}
  }, [anchorName]);
  const inspire = async () => {
    try {
      if (!state || !current) return;
      Toast.loading('鼓舞中...');
      await inspireImage({ imgId: current.id });
      await fetch();
      Toast('鼓舞成功');
    } catch ({ message }: any) {
      Toast(message);
    }
  };
  const fight = async () => {
    try {
      if (!state || !vid || !anchorName || !current) return;
      Toast.loading('挑战中...');
      const { data } = await startFight({
        vid,
        anchorName,
        toName: state.toName,
        imgId: current.id
      });
      await fetch();
      if (data === 1) {
        Toast('挑战成功');
        const parmas = new URLSearchParams(location.search);
        parmas.set('type', '1');
        history.replace({
          pathname: location.pathname,
          search: parmas.toString()
        });
      } else if (data === -1) {
        Toast('挑战失败');
      } else {
        Toast('平局');
      }
    } catch ({ response, message, code }: any) {
      if (response) {
        const {
          data: { code, message }
        } = response as any;
        if (code === 19450) await fetch(); //被挑战者变更 刷新数据
        Toast(message);
      } else if (message) {
        if (code === 19450) await fetch(); //被挑战者变更 刷新数据
        Toast(message);
      } else {
        Toast('服务器错误');
      }
    }
  };
  const changeImage = async (v: ImageV3API.Image) => {
    try {
      Toast.loading('设置中...');
      await setDefaultImage({ imgId: v.id });
      setCurrent(v);
      Toast.hide();
    } catch ({ message }: any) {
      Toast(message);
    }
  };
  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {state && (type === '1' ? '查看座驾' : '发起挑战')}
        <button
          className={styles.close}
          onClick={() => JSBridge.call('closePopup', '')}
        />
      </div>
      {state && (
        <div className={styles.content}>
          <ImageItem item={state.toUserImage} anchor={state.toNickname} />
          {type === '2' && (
            <>
              <SmallText className={styles.subtitle} fontSize={26} inline>
                我的座驾
              </SmallText>
              {state.imageList && state.imageList.length > 0 ? (
                <div className={styles.images}>
                  <div className={styles.scroller}>
                    {state.imageList.map(v => (
                      <div
                        className={classnames(
                          styles.image,
                          current?.id === v.id && styles.active
                        )}
                        key={v.id}
                        style={{ backgroundImage: `url(${v.url})` }}
                        onClick={() => changeImage(v)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.placeholder} />
              )}
            </>
          )}
          {type === '2' && current && (
            <ImageItem
              anchor="我的"
              item={current}
              inspire={state.fromInspire}
            />
          )}

          {type === '1' ? (
            <div className={styles.block}>
              <StatusItem item={state.toUserImage.talent} />
              <StatusItem item={state.toUserImage.highTalent} />
              <StatusItem item={state.toUserImage.buff} />
              <StatusItem item={state.toUserImage.deBuff} />
            </div>
          ) : (
            current && (
              <div className={styles.block}>
                <StatusItem item={current.buff} />
                <StatusItem item={current.deBuff} />
              </div>
            )
          )}
        </div>
      )}
      {type === '2' && state && current && (
        <div className={styles.buttons}>
          <button className={styles.primary} onClick={inspire}>
            鼓舞士气
            <SmallText className={styles.price} fontSize={24} inline>
              {state.price}金币/次
            </SmallText>
          </button>
          <button className={styles.accent} onClick={fight}>
            发起挑战
          </button>
        </div>
      )}
    </div>
  );
};

export default Popup;
